import React from 'react';
import { motion } from 'framer-motion';
import { X, FileText, AlertTriangle } from 'lucide-react';

interface TermsOfServiceModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const TermsOfServiceModal: React.FC<TermsOfServiceModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-2xl max-h-[90vh] sm:max-h-[85vh] bg-[#101010] border border-[#262626] rounded-xl sm:rounded-2xl shadow-[0_0_40px_rgba(49,130,246,0.2)] overflow-hidden flex flex-col"
            >
                {/* Header */}
                <div className="sticky top-0 z-10 bg-[#101010] border-b border-[#262626] px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#3182F6]/10 rounded-lg sm:rounded-xl flex items-center justify-center">
                            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-[#3182F6]" />
                        </div>
                        <h2 className="text-base sm:text-xl font-bold text-white">서비스 이용약관</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#181818] border border-[#262626] hover:border-[#363636] transition-all flex items-center justify-center"
                    >
                        <X className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#A8A8A8]" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4 overflow-y-auto flex-1">
                    {/* 중요 안내 */}
                    <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-lg sm:rounded-xl p-3 sm:p-4">
                        <div className="flex items-start gap-2 sm:gap-3">
                            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-[#FFB800] flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="text-xs sm:text-sm font-semibold text-[#FFB800] mb-1">필독 사항</h3>
                                <p className="text-[10px] sm:text-xs text-[#777777] leading-relaxed">
                                    본 약관을 주의 깊게 읽어보시기 바랍니다. 서비스 이용 시 본 약관에 동의한 것으로 간주됩니다.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Section 1: 총칙 */}
                    <div className="space-y-2 sm:space-y-3">
                        <h3 className="text-xs sm:text-sm font-semibold text-[#777777] flex items-center gap-2">
                            <span className="w-5 h-5 sm:w-6 sm:h-6 bg-[#3182F6]/10 rounded-lg flex items-center justify-center text-[10px] sm:text-xs text-[#3182F6]">1</span>
                            제1조 (목적)
                        </h3>
                        <div className="pl-6 sm:pl-8 space-y-1.5 sm:space-y-2 text-[10px] sm:text-xs text-[#777777] leading-relaxed">
                            <p>본 약관은 소셜데이터랩(이하 "회사")이 제공하는 SNS 인사이트 제공 서비스(이하 "서비스")의 이용과 관련하여 회사와 이용자 간의 권리, 의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.</p>
                            <p>본 약관은 전자상거래 등에서의 소비자보호에 관한 법률, 약관의 규제에 관한 법률, 전자문서 및 전자거래기본법, 정보통신망 이용촉진 및 정보보호 등에 관한 법률 등 관련 법령을 준수하여 작성되었습니다.</p>
                        </div>
                    </div>

                    {/* Section 2: 서비스의 제공 및 변경 */}
                    <div className="space-y-2 sm:space-y-3">
                        <h3 className="text-xs sm:text-sm font-semibold text-[#777777] flex items-center gap-2">
                            <span className="w-5 h-5 sm:w-6 sm:h-6 bg-[#3182F6]/10 rounded-lg flex items-center justify-center text-[10px] sm:text-xs text-[#3182F6]">2</span>
                            제2조 (서비스의 제공)
                        </h3>
                        <div className="pl-6 sm:pl-8 space-y-1.5 sm:space-y-2 text-[10px] sm:text-xs text-[#777777] leading-relaxed">
                            <p>회사는 다음과 같은 서비스를 제공합니다:</p>
                            <ul className="space-y-1 sm:space-y-1.5 ml-3 sm:ml-4">
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#3182F6] mt-0.5">①</span>
                                    <span>Instagram 공개 데이터를 바탕으로 한 SNS 인사이트 분석 서비스</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#3182F6] mt-0.5">②</span>
                                    <span>추정 방문 통계 및 리포트 제공 서비스</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#3182F6] mt-0.5">③</span>
                                    <span>VIP 회원을 위한 실시간 검색자 알림 서비스</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#3182F6] mt-0.5">④</span>
                                    <span>기타 회사가 정하는 부가 서비스</span>
                                </li>
                            </ul>
                            <p>회사는 서비스의 품질 향상 및 기술적 사양의 변경 등을 위하여 제공하는 서비스의 전부 또는 일부를 수정, 변경 또는 종료할 수 있으며, 이 경우 변경 내용과 적용 일자를 명시하여 서비스 내 공지사항을 통해 사전 고지합니다.</p>
                        </div>
                    </div>

                    {/* Section 3: 회원가입 및 탈퇴 */}
                    <div className="space-y-2 sm:space-y-3">
                        <h3 className="text-xs sm:text-sm font-semibold text-[#777777] flex items-center gap-2">
                            <span className="w-5 h-5 sm:w-6 sm:h-6 bg-[#3182F6]/10 rounded-lg flex items-center justify-center text-[10px] sm:text-xs text-[#3182F6]">3</span>
                            제3조 (회원가입 및 개인정보)
                        </h3>
                        <div className="pl-6 sm:pl-8 space-y-1.5 sm:space-y-2 text-[10px] sm:text-xs text-[#777777] leading-relaxed">
                            <p>회원가입은 서비스 이용을 희망하는 자(이하 "이용자")가 약관의 내용에 대하여 동의를 한 다음 회원가입 신청을 하고, 회사가 이러한 신청에 대하여 승낙함으로써 체결됩니다.</p>
                            <p>회사는 다음 각 호에 해당하는 신청에 대하여는 승낙을 거부하거나 사후에 이용계약을 해지할 수 있습니다:</p>
                            <ul className="space-y-1 sm:space-y-1.5 ml-3 sm:ml-4">
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#3182F6] mt-0.5">①</span>
                                    <span>타인의 명의를 도용하여 신청한 경우</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#3182F6] mt-0.5">②</span>
                                    <span>허위의 정보를 기재하거나, 회사가 요구하는 내용을 기재하지 않은 경우</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#3182F6] mt-0.5">③</span>
                                    <span>부정한 용도로 서비스를 이용하고자 하는 경우</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#3182F6] mt-0.5">④</span>
                                    <span>관계 법령에 위배되거나 사회의 안녕질서 또는 미풍양속을 저해할 목적으로 신청한 경우</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Section 4: 회원 탈퇴 및 자격 상실 */}
                    <div className="space-y-2 sm:space-y-3">
                        <h3 className="text-xs sm:text-sm font-semibold text-[#777777] flex items-center gap-2">
                            <span className="w-5 h-5 sm:w-6 sm:h-6 bg-[#3182F6]/10 rounded-lg flex items-center justify-center text-[10px] sm:text-xs text-[#3182F6]">4</span>
                            제4조 (회원 탈퇴 및 자격 상실)
                        </h3>
                        <div className="pl-6 sm:pl-8 space-y-2 sm:space-y-3">
                            <div className="bg-[#181818] border border-[#262626] rounded-lg sm:rounded-xl p-3 sm:p-4 space-y-1.5 sm:space-y-2">
                                <h4 className="text-[10px] sm:text-xs font-semibold text-[#777777]">바로 탈퇴 (무료)</h4>
                                <ul className="space-y-1 sm:space-y-1.5 text-[10px] sm:text-xs text-[#777777]">
                                    <li className="flex items-start gap-1.5 sm:gap-2">
                                        <span className="text-[#3182F6] mt-0.5">•</span>
                                        <span>회원은 언제든지 비용 없이 탈퇴할 수 있으며, 탈퇴를 신청하면 지체 없이 회원 자격이 상실되고 개인정보가 파기됩니다.</span>
                                    </li>
                                    <li className="flex items-start gap-1.5 sm:gap-2">
                                        <span className="text-[#3182F6] mt-0.5">•</span>
                                        <span>파기된 정보는 복구할 수 없으며, 남은 크레딧은 탈퇴와 함께 소멸하므로 환불을 원하시면 탈퇴 전에 고객센터로 신청해 주시기 바랍니다.</span>
                                    </li>
                                    <li className="flex items-start gap-1.5 sm:gap-2">
                                        <span className="text-[#3182F6] mt-0.5">•</span>
                                        <span>회원의 개인정보 삭제 요구는 회원 탈퇴와 동일하게 지체 없이 처리합니다.</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-[#181818] border border-[#262626] rounded-lg sm:rounded-xl p-3 sm:p-4 space-y-1.5 sm:space-y-2">
                                <h4 className="text-[10px] sm:text-xs font-semibold text-[#777777]">30일 뒤 탈퇴 (선택)</h4>
                                <ul className="space-y-1 sm:space-y-1.5 text-[10px] sm:text-xs text-[#777777]">
                                    <li className="flex items-start gap-1.5 sm:gap-2">
                                        <span className="text-[#3182F6] mt-0.5">•</span>
                                        <span>회원은 원하는 경우 30일 뒤에 탈퇴가 완료되도록 신청할 수 있으며, 신청일로부터 30일이 지나면 회원 자격이 상실됩니다.</span>
                                    </li>
                                    <li className="flex items-start gap-1.5 sm:gap-2">
                                        <span className="text-[#3182F6] mt-0.5">•</span>
                                        <span>30일의 기간 동안 회원은 탈퇴 신청을 철회할 수 있습니다.</span>
                                    </li>
                                    <li className="flex items-start gap-1.5 sm:gap-2">
                                        <span className="text-[#3182F6] mt-0.5">•</span>
                                        <span>회원은 이 기간 중에도 언제든지 바로 탈퇴로 전환할 수 있습니다.</span>
                                    </li>
                                </ul>
                            </div>

                            <p className="text-[10px] sm:text-xs text-[#777777] leading-relaxed">
                                회원 탈퇴 시 관련 법령 및 개인정보처리방침에 따라 회사가 회원정보를 보유하는 경우를 제외하고는 탈퇴 즉시 모든 데이터가 삭제됩니다. 단, 부정 이용 방지 및 법령상 의무 이행을 위해 일부 정보는 법정 보관 기간 동안 보관될 수 있습니다.
                            </p>
                        </div>
                    </div>

                    {/* Section 5: VIP 서비스 및 데이터 제공 */}
                    <div className="space-y-2 sm:space-y-3">
                        <h3 className="text-xs sm:text-sm font-semibold text-[#777777] flex items-center gap-2">
                            <span className="w-5 h-5 sm:w-6 sm:h-6 bg-[#FFB800]/10 rounded-lg flex items-center justify-center text-[10px] sm:text-xs text-[#FFB800]">5</span>
                            제5조 (VIP 서비스 및 데이터 제공)
                        </h3>
                        <div className="pl-6 sm:pl-8 space-y-2 sm:space-y-3">
                            <div className="bg-[#FFB800]/5 border border-[#FFB800]/20 rounded-lg sm:rounded-xl p-3 sm:p-4 space-y-1.5 sm:space-y-2">
                                <p className="text-[10px] sm:text-xs font-semibold text-[#777777]">데이터 제공 동의</p>
                                <p className="text-[10px] sm:text-xs text-[#777777] leading-relaxed">
                                    회원은 서비스 이용 시 다음 사항에 동의합니다:
                                </p>
                                <ul className="space-y-1 sm:space-y-1.5 text-[10px] sm:text-xs text-[#777777] ml-3 sm:ml-4">
                                    <li className="flex items-start gap-1.5 sm:gap-2">
                                        <span className="text-[#FFB800] mt-0.5">①</span>
                                        <span>회사는 회원가입 시 받은 개인정보 제3자 제공 동의에 따라, 회원이 분석을 요청한 Instagram 계정의 소유자인 VIP 플랜 회원에게 분석 요청 사실을 제공합니다.</span>
                                    </li>
                                    <li className="flex items-start gap-1.5 sm:gap-2">
                                        <span className="text-[#FFB800] mt-0.5">②</span>
                                        <span>제공 항목은 Instagram 아이디, 휴대전화번호, 분석 요청 일시, 분석 결과 수령 여부이며, 제공받는 자·목적·보유 기간은 개인정보처리방침에 따릅니다.</span>
                                    </li>
                                    <li className="flex items-start gap-1.5 sm:gap-2">
                                        <span className="text-[#FFB800] mt-0.5">③</span>
                                        <span>본 서비스의 특성상 회원의 활동 내역이 다른 회원에게 노출될 수 있으며, 이는 서비스 제공의 필수적인 요소임을 인지하고 이에 동의합니다.</span>
                                    </li>
                                </ul>
                                <p className="text-[10px] sm:text-xs text-[#777777] leading-relaxed mt-2 sm:mt-3">
                                    회원은 위 데이터 제공에 동의하지 않을 경우 서비스 이용이 제한될 수 있으며, 이 경우 회원 탈퇴를 통해 서비스 이용을 중단할 수 있습니다.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Section 6: 개인정보 보호 */}
                    <div className="space-y-2 sm:space-y-3">
                        <h3 className="text-xs sm:text-sm font-semibold text-[#777777] flex items-center gap-2">
                            <span className="w-5 h-5 sm:w-6 sm:h-6 bg-[#3182F6]/10 rounded-lg flex items-center justify-center text-[10px] sm:text-xs text-[#3182F6]">6</span>
                            제6조 (개인정보 보호)
                        </h3>
                        <div className="pl-6 sm:pl-8 space-y-1.5 sm:space-y-2 text-[10px] sm:text-xs text-[#777777] leading-relaxed">
                            <p>회사는 정보통신망법 및 개인정보보호법 등 관계 법령이 정하는 바에 따라 회원의 개인정보를 보호하기 위해 노력합니다. 개인정보의 보호 및 이용에 대해서는 관련 법령 및 회사의 개인정보처리방침이 적용됩니다.</p>
                            <p>회원 가입 시 수집되는 정보:</p>
                            <ul className="space-y-1 sm:space-y-1.5 ml-3 sm:ml-4">
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#3182F6] mt-0.5">•</span>
                                    <span>필수정보: 전화번호, Instagram 계정 정보</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#3182F6] mt-0.5">•</span>
                                    <span>자동수집정보: 브라우저 정보, 디바이스 정보, IP 주소, 접속 로그</span>
                                </li>
                            </ul>
                            <p className="mt-2 sm:mt-3">
                                자세한 내용은{' '}
                                <a href="/privacy" target="_blank" className="text-[#3182F6] hover:text-[#4FC3F7] underline">
                                    개인정보처리방침
                                </a>
                                을 참조하시기 바랍니다.
                            </p>
                        </div>
                    </div>

                    {/* Section 7: 회원의 의무 */}
                    <div className="space-y-2 sm:space-y-3">
                        <h3 className="text-xs sm:text-sm font-semibold text-[#777777] flex items-center gap-2">
                            <span className="w-5 h-5 sm:w-6 sm:h-6 bg-[#3182F6]/10 rounded-lg flex items-center justify-center text-[10px] sm:text-xs text-[#3182F6]">7</span>
                            제7조 (회원의 의무)
                        </h3>
                        <div className="pl-6 sm:pl-8 space-y-1.5 sm:space-y-2 text-[10px] sm:text-xs text-[#777777] leading-relaxed">
                            <p>회원은 다음 행위를 하여서는 안 됩니다:</p>
                            <ul className="space-y-1 sm:space-y-1.5 ml-3 sm:ml-4">
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#FF3040] mt-0.5">①</span>
                                    <span>신청 또는 변경 시 허위 내용의 등록</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#FF3040] mt-0.5">②</span>
                                    <span>타인의 정보 도용 또는 부정 사용</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#FF3040] mt-0.5">③</span>
                                    <span>회사가 게시한 정보의 무단 변경</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#FF3040] mt-0.5">④</span>
                                    <span>회사가 정한 정보 이외의 정보(컴퓨터 프로그램 등) 등의 송신 또는 게시</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#FF3040] mt-0.5">⑤</span>
                                    <span>회사 기타 제3자의 저작권 등 지적재산권에 대한 침해</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#FF3040] mt-0.5">⑥</span>
                                    <span>회사 기타 제3자의 명예를 손상시키거나 업무를 방해하는 행위</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#FF3040] mt-0.5">⑦</span>
                                    <span>외설 또는 폭력적인 메시지, 화상, 음성, 기타 공서양속에 반하는 정보를 공개 또는 게시하는 행위</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#FF3040] mt-0.5">⑧</span>
                                    <span>기타 불법적이거나 부당한 행위</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Section 8: 책임의 제한 */}
                    <div className="space-y-2 sm:space-y-3">
                        <h3 className="text-xs sm:text-sm font-semibold text-[#777777] flex items-center gap-2">
                            <span className="w-5 h-5 sm:w-6 sm:h-6 bg-[#3182F6]/10 rounded-lg flex items-center justify-center text-[10px] sm:text-xs text-[#3182F6]">8</span>
                            제8조 (면책조항 및 책임의 제한)
                        </h3>
                        <div className="pl-6 sm:pl-8 space-y-1.5 sm:space-y-2 text-[10px] sm:text-xs text-[#777777] leading-relaxed">
                            <p>회사는 다음 각 호의 경우에는 책임이 면제됩니다:</p>
                            <ul className="space-y-1 sm:space-y-1.5 ml-3 sm:ml-4">
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#3182F6] mt-0.5">①</span>
                                    <span>천재지변 또는 이에 준하는 불가항력으로 인하여 서비스를 제공할 수 없는 경우</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#3182F6] mt-0.5">②</span>
                                    <span>서비스용 설비의 보수, 교체, 정기점검, 공사 등 부득이한 사유로 발생한 손해</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#3182F6] mt-0.5">③</span>
                                    <span>회원의 귀책사유로 인한 서비스 이용의 장애</span>
                                </li>
                                <li className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-[#3182F6] mt-0.5">④</span>
                                    <span>제3자가 제공하는 서비스(Instagram 등)의 장애 또는 중단으로 인한 손해</span>
                                </li>
                            </ul>
                            <p className="mt-2 sm:mt-3">
                                회사는 회원이 서비스를 이용하여 기대하는 수익을 얻지 못하거나 상실한 것에 대하여 책임을 지지 않으며, 서비스를 통하여 얻은 자료로 인한 손해에 대하여 책임을 지지 않습니다.
                            </p>
                        </div>
                    </div>

                    {/* Section 9: 분쟁 해결 */}
                    <div className="space-y-2 sm:space-y-3">
                        <h3 className="text-xs sm:text-sm font-semibold text-[#777777] flex items-center gap-2">
                            <span className="w-5 h-5 sm:w-6 sm:h-6 bg-[#3182F6]/10 rounded-lg flex items-center justify-center text-[10px] sm:text-xs text-[#3182F6]">9</span>
                            제9조 (분쟁 해결)
                        </h3>
                        <div className="pl-6 sm:pl-8 space-y-1.5 sm:space-y-2 text-[10px] sm:text-xs text-[#777777] leading-relaxed">
                            <p>회사와 회원 간 발생한 전자상거래 분쟁에 관한 소송은 대한민국 법을 준거법으로 하며, 회사의 본사 소재지를 관할하는 법원을 전속 관할 법원으로 합니다.</p>
                            <p>회사는 회원으로부터 제출되는 불만사항 및 의견을 우선적으로 처리합니다. 다만, 신속한 처리가 곤란한 경우에는 회원에게 그 사유와 처리일정을 즉시 통보합니다.</p>
                            <p>회사와 회원 간에 발생한 분쟁은 전자문서 및 전자거래기본법 제32조 및 동 시행령 제21조에 의하여 설치된 전자문서·전자거래분쟁조정위원회의 조정에 따를 수 있습니다.</p>
                        </div>
                    </div>

                    {/* Last Update */}
                    <div className="pt-3 sm:pt-4 border-t border-[#262626]">
                        <p className="text-[10px] sm:text-xs text-[#777777]">
                            최종 업데이트: 2026년 10월 27일
                        </p>
                        <p className="text-[10px] sm:text-xs text-[#777777] mt-1">
                            본 약관은 관련 법령의 개정 또는 회사 정책의 변경에 따라 수정될 수 있으며, 변경 시 최소 7일 전에 서비스 내 공지사항을 통해 고지합니다. 다만, 회원에게 불리한 변경의 경우 30일 전에 고지하며, 변경된 약관에 동의하지 않을 경우 회원은 서비스 이용을 중단하고 탈퇴할 수 있습니다.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-[#101010] border-t border-[#262626] px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="w-full h-10 sm:h-12 rounded-xl bg-gradient-to-r from-[#3182F6] to-[#4FC3F7] text-white font-semibold hover:shadow-[0_0_30px_rgba(49,130,246,0.4)] transition-all text-sm sm:text-base"
                    >
                        확인
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
};
